import {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes
} from "react-router-dom";
import Swal from "sweetalert2";
import "./App.scss";
import Footer from "./infrastructure/Footer/footer.component";
import TopNavBar from "./infrastructure/TopNavBar/topNavBar.component";

import useTitleUpdater from "./hooks/useTitleUpdater";
import HomePage from "./pages/Home/home.page";
import NotFoundPage from "./pages/NotFound/notfound.page";
import { UserContext } from "./services/AppContext/appProvider";
import { useSocket } from "./services/SocketContext";
import { ThemeProvider } from "./services/ThemeContext";
import { isSharedProduct } from "./utilities/dataValidators";

// Modals
import LogRocket from "logrocket";
import { AiChat } from "./components/AiChat/aiChat.component";
import { AiChatNotification } from "./components/AiChat/aiChatNotification";
import ModalNewBid from "./components/Modals/ModalNewBid/modalNewBid.component";
import ModalNewReward from "./components/Modals/ModalNewReward/modalNewReward.component";
// import UpdatePrompt from "./components/Modals/ModalServiceWorkerVersion/modalServiceWorkerVersion.component";
import ModalSignup from "./components/Modals/ModalSignup/modalSignup.component";
import ModalTerms from "./components/Modals/ModalTerms/modalTerms.component";
import { fetchUserDailyCheckIn } from "./models/user.model";
import {
  checkExclude,
  systemErrorAlertText,
  utilLogOut
} from "./utilities/utils";

// Modals Lazy Loaded
const ModalMajorAuction = lazy(
  () =>
    import("./components/Modals/ModalMajorAuction/modalMajorAuction.component")
);
const ModalCheckIn = lazy(
  () => import("./components/Modals/ModalCheckIn/modalCheckIn.component")
);
const ModalShare = lazy(
  () => import("./components/Modals/ModalShare/modalShare.component")
);
const ModalLaunch = lazy(
  () => import("./components/Modals/ModalLaunch/modalLaunch.component")
);

const CartFloatingIcon = lazy(
  () => import("./components/CartFloatingIcon/cartFloatingIcon.component")
);

// Pages Lazy Loaded
// const HomePage = lazy(() => import("./pages/Home/home.page"));
// const NotFoundPage = lazy(() => import("./pages/NotFound/notfound.page"));
const SearchPage = lazy(() => import("./pages/Search/search.page"));
const SetupAccountPage = lazy(
  () => import("./pages/SetupAccount/setupAccount.page")
);
const AuctionItemPage = lazy(
  () => import("./pages/AuctionItem/auctionItem.page")
);
const BuyPacksPage = lazy(() => import("./pages/BuyPacks/buyPacks.page"));
const ResetPasswordPage = lazy(
  () => import("./pages/ResetPassword/resetPassword.page")
);
const LoginPage = lazy(() => import("./pages/Login/Login.page"));
const AuthingTokenPage = lazy(
  () => import("./pages/AuthingToken/authingToken.page")
);
// const CategoryPage = lazy(() => import("./pages/Category/category.page"));
const WinnersPage = lazy(() => import("./pages/Winners/winners.page"));
const AboutPage = lazy(() => import("./pages/About/about.page"));
const GuidePage = lazy(() => import("./pages/Guide/guide.page"));
const ContactUs = lazy(() => import("./pages/Contact/contactUs.page"));
const BillboardPage = lazy(() => import("./pages/Billboard/billboard.page"));
const BuyNowPage = lazy(() => import("./pages/BuyNow/buyNow.page"));
const DashboardPage = lazy(() => import("./pages/Dashboard/dashboard.page"));
const ModalWinner = lazy(
  () => import("./components/Modals/ModalWinner/modalWinner.component")
);

const TitleUpdater = ({ title }: { title?: string }) => {
  useTitleUpdater(title);
  return null;
};

// Component to handle redirection for /admin path
const AdminRedirect = () => {
  useEffect(() => {
    // Redirect to a different site for /admin path
    const adminLink = process.env.REACT_APP_ADMIN_LINK;
    if (adminLink) {
      window.location.href = adminLink;
    } else {
      console.error("Admin link not set");
      window.location.href = "/";
    }
  }, []);

  return null; // Return null because this component only handles redirection
};

function App() {
  const { t } = useTranslation();

  const socketController = useSocket();
  const {
    isLoggedIn,
    user,
    setSocketAuthenticated,
    setNumBids,
    shareLink,
    setFavoriteProducts,
    setCartItems,
    cartItems
  } = useContext(UserContext);

  const [nextMajorAuction, setNextMajorAuction] = useState<ShortProduct | null>(
    null
  );
  const [billboardPageActive, setBillboardPageActive] =
    useState<boolean>(false);
  const [adminPage, setAdminPage] = useState<boolean>(false);
  const [checkInModalActive, setCheckInModalActive] = useState<boolean>(false);
  const [newReward, setNewReward] = useState<NewRewardPopup | null>(null);
  const [showNotification, setShowNotification] = useState<
    "newBid" | "newReward" | null
  >(null);

  // const [launchModalActive, setLaunchModalActive] = useState<boolean>(false);

  const authenticate = useCallback(
    async (nickname: string, bid_token: string) => {
      console.log("Authenticating socket");
      const response = await socketController.authenticate(nickname, bid_token);

      if (response.rStatus === "success") {
        setSocketAuthenticated(true);
      } else {
        setSocketAuthenticated(false);
        console.log(response);
        Swal.fire({
          title: "Failed to authenticate",
          html:
            `<strong class="font-extrabold">Please login again</strong>` +
            systemErrorAlertText(),
          icon: "error",
          confirmButtonText: "Logout",
          customClass: {
            popup: "!rounded-lg",
            confirmButton:
              "!bg-secondary !outline-none !border-none !shadow-none"
          }
        }).then((result) => {
          utilLogOut();
        });
      }
    },
    [setSocketAuthenticated, socketController]
  );

  useLayoutEffect(() => {
    if (isLoggedIn && user) {
      authenticate(user.nickname, user.bidToken);
    }
  }, [isLoggedIn, socketController, user, authenticate]);

  const handleUpdateBids = useCallback(
    async (
      response:
        | {
            rStatus: "success";
            rData: { numBids: number };
          }
        | { rStatus: "error"; rData: string }
    ) => {
      if (response.rStatus === "error") {
        console.log(response.rData);
        return;
      }
      setNumBids(response.rData.numBids);
    },
    [setNumBids]
  );

  const handleUpdateFavs = useCallback(
    async (
      response:
        | { rStatus: "success"; rData: { favoriteProducts: number[] } }
        | { rStatus: "error"; rData: string }
    ) => {
      if (response.rStatus === "error") {
        console.log(response.rData);
        return;
      }
      setFavoriteProducts(response.rData.favoriteProducts);
    },
    [setFavoriteProducts]
  );

  const handleAddToCart = useCallback(
    async (
      response:
        | { rStatus: "success"; rData: { cartItems: CartItem[] } }
        | { rStatus: "error"; rData: string }
    ) => {
      console.log(response);
      if (response.rStatus === "error") {
        console.log(response.rData);
        setCartItems([]);
        return;
      }
      console.log(response.rData.cartItems, "Received add-to-cart");

      // check to see if items have changed
      if (
        JSON.stringify(response.rData.cartItems) === JSON.stringify(cartItems)
      ) {
        return;
      }
      setCartItems(response.rData.cartItems);
    },
    [cartItems, setCartItems]
  );

  useEffect(() => {
    // socketController.connect();
    socketController.on("new-bids-count", handleUpdateBids);
    socketController.on("favorite-product", handleUpdateFavs);
    socketController.on("test-emit", () => console.log("test-emit")); // WebSocket event for cart updates
    socketController.on("add-to-cart", handleAddToCart);
    // return () => socketController.disconnect();
  }, [socketController, handleUpdateBids, handleUpdateFavs, handleAddToCart]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const referrerToken = urlParams.get("referrerToken");
    if (referrerToken) {
      localStorage.setItem("referrerToken", referrerToken);
    }
  }, []);

  useEffect(() => {
    const nextMajorAuction = localStorage.getItem("nextMajorAuction");
    if (nextMajorAuction && isSharedProduct(JSON.parse(nextMajorAuction))) {
      setNextMajorAuction(JSON.parse(nextMajorAuction));
    }
  }, []);

  function closeNextMajorAuctionModal() {
    setNextMajorAuction(null);
    localStorage.removeItem("nextMajorAuction");
  }

  // This is to prevent the check-in pop up from appearing after refreshing the page
  useEffect(() => {
    if (!isLoggedIn) return;

    async function checkIfUserCheckedInToday() {
      const response = await fetchUserDailyCheckIn();

      if (response.rStatus === "error") {
        console.error(response.message);
        return;
      }

      if (response.rData.checkedIn) {
        return;
      }

      setCheckInModalActive(true);
    }

    checkIfUserCheckedInToday();
  }, [isLoggedIn]);

  // useEffect(() => {
  //   const signUpEmail = getLocStorageSignupEmail();
  //   if (isLoggedIn || signUpEmail) {
  //     setLaunchModalActive(false);
  //     return;
  //   }
  //   const hideOnPages = [
  //     "/login",
  //     "/signup",
  //     "/reset-password",
  //     "/setup-account",
  //     "/auth",
  //     "/billboard"
  //   ];

  //   const path = window.location.pathname;
  //   if (hideOnPages.some((page) => path.includes(page))) {
  //     setLaunchModalActive(false);
  //     return;
  //   }

  //   // get launch modal shown status from local storage
  //   const launchModalShown = localStorage.getItem("launchModalShown");
  //   if (!launchModalShown) {
  //     setLaunchModalActive(true);
  //   }
  // }, [isLoggedIn]);

  const Loader = () => (
    <div className="text-center">
      <span className="loading loading-ring loading-lg m-1"></span>
    </div>
  );

  useEffect(() => {
    const logRocketUserIdentify = async () => {
      if (!isLoggedIn || !user) {
        return;
      }
      const environment = process.env.REACT_APP_ENVIRONMENT;
      if (!environment) {
        console.error("Environment not set");
        return;
      }

      if (environment !== "production" && environment !== "staging") {
        return;
      }

      const exclude = await checkExclude();

      if (exclude) {
        return;
      }

      const logRocketID = process.env.REACT_APP_LOG_ROCKET_ID;
      if (!logRocketID) {
        console.error("LogRocket ID not set");
        return;
      }

      // initialize LogRocket user
      LogRocket.identify(user.uuid, {
        name: user.nickname,
        email: user.email,
        environment: environment
      });
    };

    logRocketUserIdentify();
  }, [user, isLoggedIn]);

  return (
    <ThemeProvider>
      <div
        className={`App ${billboardPageActive || adminPage ? "billboardActive" : ""}`}
      >
        <Router>
          {!(billboardPageActive || adminPage) && (
            <header>
              <TopNavBar />
            </header>
          )}

          <div className="page-content">
            <Routes>
              <Route path="/admin" element={<AdminRedirect />} />
              <Route
                path="/"
                element={
                  <>
                    <TitleUpdater title={t("Smart Shopping, Real Value")} />
                    <HomePage />
                  </>
                }
              />
              <Route
                path="/setup-account"
                element={
                  <>
                    <TitleUpdater title={t("Setup Account")} />
                    <div className="container mx-auto">
                      <h1 className="text m-5 text-center text-3xl font-bold">
                        {t("Please check your link")}
                      </h1>
                    </div>
                  </>
                }
              />

              <Route
                path="/reset-password"
                element={
                  <>
                    <TitleUpdater title={t("Reset Password")} />
                    <div className="container mx-auto">
                      <h1 className="text m-5 text-center text-3xl font-bold">
                        {t("Please check your link")}
                      </h1>
                    </div>
                  </>
                }
              />

              <Route
                path="/reset-password/:token"
                element={
                  <>
                    <TitleUpdater title={t("Reset Password")} />
                    <Suspense fallback={<Loader />}>
                      <ResetPasswordPage />
                    </Suspense>
                  </>
                }
              />

              {/* <Route
              path="/category/:categoryID"
              element={
                <Suspense fallback={<Loader />}>
                  <CategoryPage />
                </Suspense>
              }
            /> */}

              <Route
                path="/setup-account/:token"
                element={
                  <>
                    <TitleUpdater title={t("Setup Account")} />
                    <Suspense fallback={<Loader />}>
                      <SetupAccountPage />
                    </Suspense>
                  </>
                }
              />

              <Route
                path="/item/:auctionItemID"
                element={
                  <>
                    <Suspense fallback={<Loader />}>
                      <AuctionItemPage />
                    </Suspense>
                  </>
                }
              />

              <Route
                path="/buy-packs"
                element={
                  <>
                    <TitleUpdater title={t("Buy Packs")} />
                    <Suspense fallback={<Loader />}>
                      <BuyPacksPage />
                    </Suspense>
                  </>
                }
              />

              <Route
                path="/dashboard"
                element={
                  <>
                    <TitleUpdater title={t("Dashboard")} />
                    <Suspense fallback={<Loader />}>
                      <DashboardPage />
                    </Suspense>
                  </>
                }
              />

              <Route
                path="/winners"
                element={
                  <>
                    <TitleUpdater title={t("Winners")} />
                    <Suspense fallback={<Loader />}>
                      <WinnersPage />
                    </Suspense>
                  </>
                }
              />

              <Route
                path="/login"
                element={
                  <>
                    <TitleUpdater title={t("Login")} />
                    <Suspense fallback={<Loader />}>
                      <LoginPage />
                    </Suspense>
                  </>
                }
              />

              <Route
                path="/signup"
                element={
                  <>
                    <TitleUpdater title={t("Sign Up")} />
                    <Suspense fallback={<Loader />}>
                      <ModalSignup
                        closeModal={() => {
                          window.location.href = "/";
                        }}
                        modalType="page"
                      />
                    </Suspense>
                  </>
                }
              />

              <Route
                path="/help/time"
                element={
                  <div className="container mx-auto">
                    <h1 className="text m-5 text-center text-3xl font-bold">
                      {t("Please check your time")}
                    </h1>
                  </div>
                }
              />

              <Route
                path="/auth"
                element={
                  <div className="container mx-auto">
                    <h1 className="text m-5 text-center text-3xl font-bold">
                      {t("Please check your link")}
                    </h1>
                  </div>
                }
              />

              <Route
                path="/search"
                element={
                  <>
                    <TitleUpdater title={t("Search")} />
                    <Suspense fallback={<Loader />}>
                      <SearchPage />
                    </Suspense>
                  </>
                }
              />

              <Route
                path="/auth/:jwtToken"
                element={
                  <>
                    <TitleUpdater title={t("Authenticating")} />
                    <Suspense fallback={<Loader />}>
                      <AuthingTokenPage />
                    </Suspense>
                  </>
                }
              />

              <Route
                path="/about"
                element={
                  <>
                    <TitleUpdater title={t("About")} />
                    <Suspense fallback={<Loader />}>
                      <AboutPage />
                    </Suspense>
                  </>
                }
              />

              <Route
                path="/guide"
                element={
                  <>
                    <TitleUpdater title={t("Guide")} />
                    <Suspense fallback={<Loader />}>
                      <GuidePage />
                    </Suspense>
                  </>
                }
              />

              <Route
                path="/contact-us"
                element={
                  <>
                    <TitleUpdater title={t("Contact Us")} />
                    <Suspense fallback={<Loader />}>
                      <ContactUs />
                    </Suspense>
                  </>
                }
              />

              <Route
                path="/buy-now/:auctionItemID"
                element={
                  <>
                    <TitleUpdater title={t("Buy Now")} />
                    <Suspense fallback={<Loader />}>
                      <BuyNowPage />
                    </Suspense>
                  </>
                }
              />

              <Route
                path="/billboard"
                element={
                  <>
                    <TitleUpdater title={t("Billboard")} />
                    <Suspense fallback={<Loader />}>
                      <BillboardPage setActive={setBillboardPageActive} />
                    </Suspense>
                  </>
                }
              />

              <Route
                path="/termsAndConditions"
                element={
                  <Navigate to={"/"} replace state={{ showTerms: true }} />
                }
              />

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </div>
          {!(billboardPageActive || adminPage) && <Footer />}
          {nextMajorAuction && (
            <Suspense fallback={<></>}>
              <ModalMajorAuction
                nextMajorAuction={nextMajorAuction}
                closeModal={closeNextMajorAuctionModal}
              />
            </Suspense>
          )}

          {!nextMajorAuction && checkInModalActive && (
            <Suspense fallback={<></>}>
              <ModalCheckIn
                closeModal={async () => {
                  setCheckInModalActive(false);
                }}
              />
            </Suspense>
          )}

          {/* {launchModalActive && (
            <Suspense fallback={<></>}>
              <ModalLaunch closeModal={() => setLaunchModalActive(false)} />
            </Suspense>
          )} */}

          <ModalTerms />
          <Suspense fallback={<></>}>
            <CartFloatingIcon />
          </Suspense>
          <Suspense fallback={<></>}>
            <ModalWinner />
          </Suspense>
          <AiChat />
          <AiChatNotification />
          {/* <UpdatePrompt /> */}
        </Router>

        {shareLink && (
          <Suspense fallback={<></>}>
            <ModalShare />
          </Suspense>
        )}

        {!billboardPageActive && (
          <ModalNewBid setShowNotification={setShowNotification} />
        )}

        {!billboardPageActive && (
          <ModalNewReward setShowNotification={setShowNotification} />
        )}
      </div>
    </ThemeProvider>
  );
}

export default App;
